import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tournamentsApi } from '../services/api';
import { Tournament } from '../types';
import DeckSubmissionModal from '../components/DeckSubmissionModal';

const DeckSubmission: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tournamentId = searchParams.get('tournament');

  useEffect(() => {
    const fetchTournament = async () => {
      if (!tournamentId) {
        setError('No tournament specified');
        setLoading(false);
        return;
      }

      try {
        const response = await tournamentsApi.getById(tournamentId);
        setTournament(response.data);
      } catch (err) {
        console.error('Failed to fetch tournament:', err);
        setError('Failed to load tournament');
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  const handleClose = () => {
    // Navigate back to tournaments page
    navigate('/tournaments');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tournament...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-800 mb-2">Error</h1>
            <p className="text-red-600 mb-4">{error || 'Tournament not found'}</p>
            <button
              onClick={() => navigate('/tournaments')}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Back to Tournaments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/tournaments')}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
          >
            ← Back to Tournaments
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Submit Deck</h1>
          <p className="text-gray-600 mt-2">
            Submit a deck for <strong>{tournament.name}</strong>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Tournament Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Tournament:</span>
                <p className="text-gray-900">{tournament.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Format:</span>
                <p className="text-gray-900">{tournament.ruleset?.format || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Budget Limit:</span>
                <p className="text-gray-900">
                  {tournament.ruleset?.budget_limit ? `€${tournament.ruleset.budget_limit}` : 'No limit'}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Status:</span>
                <p className="text-gray-900">{tournament.is_active ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          </div>

          <DeckSubmissionModal
            tournament={tournament}
            isOpen={true}
            onClose={handleClose}
          />
        </div>
      </div>
    </div>
  );
};

export default DeckSubmission;
